import { useMemo } from "react";
import { addDays, differenceInDays, eachDayOfInterval, isAfter, isBefore, max, min, subDays } from "../../../../date-fns/index.js";
import { useCalendarConfiguration } from "../../context/CalendarRoot.js";
import { defaultMinDate } from "../../context/constants.js";
import { formatDate, parseDate } from "../../context/utils.js";
const emptySelectionAsRecord = {};
function useSelectionData(props) {
  const {
    selectionStart: selectionStartInput,
    selectionEnd: selectionEndInput,
    maxIntervalLength: maxIntervalLengthInput,
    lastSelectableDate
  } = props;
  const {
    minDate: rawMinDate,
    maxDate: rawMaxDate,
    firstNonArchivedDay: rawFirstNonArchivedDay,
    dateFnsOptions
  } = useCalendarConfiguration();
  const minDate = parseDate(rawMinDate, dateFnsOptions);
  const maxDate = parseDate(rawMaxDate, dateFnsOptions);
  const defaultMaxIntervalLength = !!minDate && !!maxDate ? Math.abs(differenceInDays(minDate, maxDate)) : 10;
  const maxIntervalLength = maxIntervalLengthInput ?? defaultMaxIntervalLength;
  return useMemo(() => {
    if (!selectionStartInput) {
      return {
        selection: emptySelectionAsRecord,
        selectableInterval: void 0,
        edgeOfSelection: emptySelectionAsRecord
      };
    }
    const isBackwardSelection = selectionEndInput && isAfter(selectionStartInput, selectionEndInput);
    const selectionStart = isBackwardSelection ? selectionEndInput : selectionStartInput;
    const selectionEnd = isBackwardSelection ? selectionStartInput : selectionEndInput || selectionStartInput;
    const minDate2 = rawMinDate ? parseDate(rawMinDate, dateFnsOptions) : defaultMinDate;
    const selectedDays = isBackwardSelection ? [selectionEnd] : eachDayOfInterval({
      start: selectionStart,
      end: selectionEnd
    });
    const firstNonArchivedDate = rawFirstNonArchivedDay ? parseDate(rawFirstNonArchivedDay, dateFnsOptions) : void 0;
    const selectingArchivedDays = firstNonArchivedDate ? isBefore(selectionStart, firstNonArchivedDate) : false;
    let selectableDayStart = minDate2;
    if (!selectionEndInput) {
      if (maxIntervalLength) {
        selectableDayStart = subDays(selectionStart, maxIntervalLength);
      }
      if (!selectingArchivedDays && firstNonArchivedDate) {
        selectableDayStart = max([firstNonArchivedDate, selectableDayStart]);
      }
    }
    let selectableDayEnd = maxDate;
    if (!selectionEndInput) {
      if (maxIntervalLength) {
        selectableDayEnd = addDays(selectionStart, maxIntervalLength);
      }
      if (selectingArchivedDays && firstNonArchivedDate) {
        selectableDayEnd = min([subDays(firstNonArchivedDate, 1), selectableDayEnd]);
      }
      if (lastSelectableDate) {
        selectableDayEnd = min([selectableDayEnd, lastSelectableDate]);
      }
    }
    const selectableDays = eachDayOfInterval({
      start: selectableDayStart,
      end: selectableDayEnd
    });
    const selection = selectedDays.reduce((acc, date) => {
      const dateAsString = formatDate(date, dateFnsOptions);
      acc[dateAsString] = "rangeMiddle";
      return acc;
    }, {});
    const selectableInterval = selectableDays.reduce((acc, date) => {
      const dateAsString = formatDate(date, dateFnsOptions);
      acc.add(dateAsString);
      return acc;
    }, /* @__PURE__ */ new Set());
    const start = formatDate(selectionStart, dateFnsOptions);
    const end = formatDate(selectionEnd, dateFnsOptions);
    selection[end] = "rangeEnd";
    selection[start] = "rangeStart";
    const edgeOfSelection = {
      [end]: "rangeEnd",
      [start]: "rangeStart"
    };
    return {
      selection,
      selectableInterval,
      edgeOfSelection
    };
  }, [
    selectionStartInput,
    selectionEndInput,
    rawMinDate,
    dateFnsOptions,
    rawFirstNonArchivedDay,
    maxDate,
    maxIntervalLength,
    lastSelectableDate
  ]);
}
export {
  useSelectionData
};
//# sourceMappingURL=useSelectionData.js.map
